import { FormEvent, useState } from "react";
import axios from "axios";

export type ContactFields = {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
};

type Status = "idle" | "sending" | "success" | "error";

const EMPTY: ContactFields = { name: "", email: "", phone: "", company: "", message: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Shared contact-form logic. Sends the same `{ name, email, phone, message }`
 * JSON the old site sent to REACT_APP_BASE_URL, so the backend needs no change.
 * Company and selected services are folded into `message`.
 */
export function useContactForm(initialServices: string[] = []) {
  const [fields, setFields] = useState<ContactFields>(EMPTY);
  const [services, setServices] = useState<string[]>(initialServices);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFields, string>>>({});
  const [status, setStatus] = useState<Status>("idle");

  const setField = (key: keyof ContactFields) => (value: string) => {
    setFields((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const toggleService = (service: string) =>
    setServices((s) => (s.includes(service) ? s.filter((x) => x !== service) : [...s, service]));

  const validate = () => {
    const next: typeof errors = {};
    if (!fields.name.trim()) next.name = "Please enter your name.";
    if (!EMAIL_RE.test(fields.email.trim())) next.email = "Please enter a valid email.";
    if (!fields.message.trim()) next.message = "Tell us a little about your project.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (status === "sending" || !validate()) return;

    const extra = [
      fields.company.trim() && `Company: ${fields.company.trim()}`,
      services.length > 0 && `Interested in: ${services.join(", ")}`,
    ].filter(Boolean);

    setStatus("sending");
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}`,
        {
          name: fields.name.trim(),
          email: fields.email.trim(),
          phone: fields.phone.trim(),
          message: [fields.message.trim(), ...extra].join("\n\n"),
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setStatus("success");
      setFields(EMPTY);
      setServices(initialServices);
    } catch {
      setStatus("error");
    }
  };

  return { fields, setField, services, toggleService, errors, status, submit };
}
